/**
 * Cómo se ven los precios en toda la aplicación: pesos colombianos con punto
 * de miles y sin decimales, "$100.000".
 *
 * Solo es presentación. Lo que se guarda y se calcula sigue siendo el número
 * tal cual (100000): estas funciones nunca se usan para escribir datos.
 *
 * Se hace a mano y no con `Intl.NumberFormat('es-CO')` para que se vea igual
 * en todos los navegadores: según la versión, Intl agrupa o no los números de
 * cuatro cifras ("1000" frente a "1.000") y mete un espacio duro tras el "$".
 */

/** 100000 → "100.000". Redondea al peso: en COP no se usan centavos. */
export const separarMiles = (valor: number): string =>
  String(Math.round(Math.abs(valor))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * 100000 → "$100.000"; -2500 → "-$2.500". Acepta también el número escrito
 * como texto ("180000"), que es como lo guardaban algunas reservas antiguas.
 * Lo que no es un número se muestra como "$0".
 */
export const formatoPesos = (valor: number | string | null | undefined): string => {
  const n = typeof valor === 'number' ? valor : Number(valor ?? 0);
  if (!Number.isFinite(n)) return '$0';
  const redondeado = Math.round(n);
  return `${redondeado < 0 ? '-' : ''}$${separarMiles(redondeado)}`;
};

/** Con la misma forma que `Intl.NumberFormat`, para cambiarlo sin tocar cada uso. */
export const pesos = { format: (valor: number | string | null | undefined) => formatoPesos(valor) };

/** Máximo de cifras de un campo de dinero: hasta $999.999.999.999. */
export const MAX_CIFRAS = 12;

/**
 * Lo que alguien escribe en un campo de dinero → solo las cifras, sin ceros a
 * la izquierda: "$ 180.000" → "180000"; "" → "".
 */
export const soloCifras = (texto: string): string => {
  const cifras = String(texto ?? '').replace(/\D/g, '').slice(0, MAX_CIFRAS);
  return cifras.replace(/^0+(?=\d)/, '');
};

/**
 * Para lo que se PEGA en un campo de dinero, que puede traer centavos:
 * "180.000,00" o "180,000.50" → "180000". Una coma o un punto seguidos de una
 * o dos cifras al final son decimales y se descartan; con tres cifras son
 * miles ("1.500" → "1500"). Al teclear no se usa: mientras se borra, "180.00"
 * es un paso intermedio de "180.000", no 180 pesos.
 */
export const cifrasDePegado = (texto: string): string =>
  soloCifras(String(texto ?? '').trim().replace(/[.,]\d{1,2}$/, ''));
